import { Reducer, Action } from 'redux';
import { call, select, all, delay, put, takeEvery, fork } from 'redux-saga/effects';
import { actionCreators as layoutActionCreators } from 'src/reducers/Layout';
import { selectors as authSelectors } from 'src/reducers/Auth';
import Api from '../api';
import DocumentTemplate from 'src/models/DocumentTemplate';

export interface IDocumentTemplatesState {
    documentTemplates: Array<DocumentTemplate>;
    selectedTemplates: Array<DocumentTemplate>;
}

const initialState: IDocumentTemplatesState = {
    documentTemplates: undefined,
    selectedTemplates: undefined
};

export enum actionTypes {
    GET_DOCUMENT_TEMPLATES = '[documentTemplates] GET_DOCUMENT_TEMPLATES',
    GET_DOCUMENT_TEMPLATES_SUCCESS = '[documentTemplates] GET_DOCUMENT_TEMPLATES_SUCCESS',
    GET_DOCUMENT_TEMPLATES_FOR_PROJECT = '[documentTemplates] GET_DOCUMENT_TEMPLATES_FOR_PROJECT',
    GET_DOCUMENT_TEMPLATES_FOR_PROJECT_SUCCESS = '[documentTemplates] GET_DOCUMENT_TEMPLATES_FOR_PROJECT_SUCCESS',
    ATTACH_DOCUMENT_TEMPLATES = '[documentTemplates] ATTACH_DOCUMENT_TEMPLATES',
    REMOVE_DOCUMENT_TEMPLATES = '[documentTemplates] REMOVE_DOCUMENT_TEMPLATES',
    CLEAR_TEMPLATES = '[documentTemplates] CLEAR_TEMPLATES'
}

export const actionCreators = {
    getDocumentTemplates: (clientNumber: number, projectNumber: number) => {
        return { type: actionTypes.GET_DOCUMENT_TEMPLATES, payload: { clientNumber, projectNumber } };
    },

    getDocumentTemplatesSuccess: (documentTemplates: Array<DocumentTemplate>) => {
        return { type: actionTypes.GET_DOCUMENT_TEMPLATES_SUCCESS, payload: documentTemplates };
    },

    getDocumentTemplatesForProject: (clientNumber: number, projectNumber: number) => {
        return { type: actionTypes.GET_DOCUMENT_TEMPLATES_FOR_PROJECT, payload: { clientNumber, projectNumber } };
    },

    getDocumentTemplatesForProjectSuccess: (documentTemplates: Array<DocumentTemplate>) => {
        return { type: actionTypes.GET_DOCUMENT_TEMPLATES_FOR_PROJECT_SUCCESS, payload: documentTemplates };
    },

    attachDocumentTemplates: (clientNumber: number, projectNumber: number, documentTemplateIds: Array<number>) => {
        return { type: actionTypes.ATTACH_DOCUMENT_TEMPLATES, payload: { clientNumber, projectNumber, documentTemplateIds } };
    },

    removeFromProject: (clientNumber: number, projectNumber: number, attachedIds: Array<number>) => {
        return { type: actionTypes.REMOVE_DOCUMENT_TEMPLATES, payload: { clientNumber, projectNumber, attachedIds } };
    },

    clearTemplates: () => {
        return { type: actionTypes.CLEAR_TEMPLATES };
    }
};

const reducer: Reducer<IDocumentTemplatesState> = (
    state: IDocumentTemplatesState = initialState,
    action: Action
): IDocumentTemplatesState => {
    switch (action.type) {
        case actionTypes.GET_DOCUMENT_TEMPLATES_SUCCESS: {
            const payload = (action as { type: string; payload: Array<DocumentTemplate> }).payload;
            return {
                ...state,
                documentTemplates: payload
            };
        }
        case actionTypes.GET_DOCUMENT_TEMPLATES_FOR_PROJECT_SUCCESS: {
            const payload = (action as { type: string; payload: Array<DocumentTemplate> }).payload;
            return {
                ...state,
                selectedTemplates: payload
            };
        }
        case actionTypes.CLEAR_TEMPLATES: {
            return {
                ...state,
                selectedTemplates: undefined,
                documentTemplates: undefined
            };
        }
        default:
            return state;
    }
};

export default reducer;

const sagas = {
    getDocumentTemplates: function* (action: { type: string; payload: { clientNumber: number, projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        const [result] = yield all([
            call(Api.DocumentTemplates.get, action.payload.clientNumber, action.payload.projectNumber, jwt),
            delay(200)
        ]);
        yield put(actionCreators.getDocumentTemplatesSuccess(result));
    },
    getDocumentTemplatesByProject: function* (action: { type: string; payload: { clientNumber: number, projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        const [result] = yield all([
            call(Api.DocumentTemplates.getFromProject, action.payload.clientNumber, action.payload.projectNumber, jwt),
            delay(200)
        ]);
        yield put(actionCreators.getDocumentTemplatesForProjectSuccess(result));
    },
    attachDocumentTemplates: function* (action: { type: string; payload: { clientNumber: number, projectNumber: number, documentTemplateIds: Array<number> } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        yield all([
            call(
                Api.DocumentTemplates.attach,
                action.payload.clientNumber,
                action.payload.projectNumber,
                action.payload.documentTemplateIds,
                jwt
            ),
            delay(500),
        ]);
        yield put(actionCreators.clearTemplates());
        yield put(actionCreators.getDocumentTemplatesForProject(action.payload.clientNumber, action.payload.projectNumber));
    },
    removeFromProject: function* (action: { type: string; payload: { clientNumber: number, projectNumber: number, removeIds: Array<number> } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        yield all([
            call(
                Api.DocumentTemplates.remove,
                action.payload.clientNumber,
                action.payload.projectNumber,
                action.payload.removeIds,
                jwt
            ),
            delay(500),
        ]);
        yield put(actionCreators.clearTemplates());
        yield put(actionCreators.getDocumentTemplatesForProject(action.payload.clientNumber, action.payload.projectNumber));
    }
};

const watches = {
    getDocumentTemplates: function* () {
        yield takeEvery(actionTypes.GET_DOCUMENT_TEMPLATES, sagas.getDocumentTemplates);
    },
    getDocumentTemplatesForProject: function* () {
        yield takeEvery(actionTypes.GET_DOCUMENT_TEMPLATES_FOR_PROJECT, sagas.getDocumentTemplatesByProject);
    },
    attachDocumentTemplates: function* () {
        yield takeEvery(actionTypes.ATTACH_DOCUMENT_TEMPLATES, sagas.attachDocumentTemplates);
    },
    removeDocumentTemplates: function* () {
        yield takeEvery(actionTypes.REMOVE_DOCUMENT_TEMPLATES, sagas.removeFromProject);
    }
};

export function* saga() {
    yield fork(watches.getDocumentTemplates);
    yield fork(watches.getDocumentTemplatesForProject);
    yield fork(watches.attachDocumentTemplates);
    yield fork(watches.removeDocumentTemplates);
}


// WEBPACK FOOTER //
// ./src/reducers/DocumentTemplates.ts